import { type AxiosInstance } from "axios"
import handleQueryResult from "./util/handleQueryResult"

export type SpireApiOptions = {
	workerPullUpToYesterday: boolean
}

export type SpireImportRecord = {
	responseRecordId: string
	recordId: string
	requestName: string
	requestOverridden: boolean
	content: string | undefined
}

export type SpireIntegrationApiResponse = {
	requestName: string
	requestOverridden: boolean
	requestFromDate?: string
	requestToDate: string
	requestNoOfRecords: number
	requestLastRecordId?: string
	statusCode?: number
	lastRecordId?: string
	recordCount?: number
	content?: string
	errorMessage?: string
}

export type SpireImportApiRequestModel =
	| {
			fromDate: string
			toDate: string
			noOfRecords: number
	  }
	| {
			lastRecordId: string
			toDate: string
			noOfRecords: number
	  }

class SpireIntegrationsClient {
	constructor(private axiosClient: AxiosInstance) {}

	public getApiOptions = () =>
		handleQueryResult(
			this.axiosClient.get<SpireApiOptions>(
				`/api/admin/integrations/bridge/spire/options`
			)
		)

	public updateTestOptionWorkerPullUpToYesterday = (setting: boolean) =>
		handleQueryResult(
			this.axiosClient.post<void>(
				`/api/admin/integrations/bridge/spire/options/update/workerPullUpToYesterday`,
				setting,
				{
					headers: {
						"Content-Type": "application/json"
					}
				}
			)
		)

	public startImport = async () =>
		this.axiosClient.post<void>(
			`/api/admin/integrations/bridge/spire/import`
		)

	public startAdHocImport = (request: SpireImportApiRequestModel) =>
		this.axiosClient.post<void>(
			`/api/admin/integrations/bridge/spire/import/adhoc`,
			request
		)

	public replayApiResponse = (responseRecordId: string) => {
		const params = new URLSearchParams()
		params.append("responseRecordId", responseRecordId)
		return this.axiosClient.post<void>(
			`/api/admin/import/unpacktransformmerge/spire?${params.toString()}`
		)
	}

	public getImportRecords = ({
		pageIndex,
		pageSize
	}: {
		pageIndex: number
		pageSize: number
	}) => {
		const query = new URLSearchParams()
		query.append("pageIndex", pageIndex.toString())
		query.append("pageSize", pageSize.toString())

		return handleQueryResult(
			this.axiosClient.get<{
				importRecords: SpireImportRecord[]
				total: number
			}>(
				`/api/admin/integrations/bridge/spire/records?${query.toString()}`
			)
		)
	}

	public getApiResponses = ({
		pageIndex,
		pageSize
	}: {
		pageIndex: number
		pageSize: number
	}) => {
		const query = new URLSearchParams()
		query.append("pageIndex", pageIndex.toString())
		query.append("pageSize", pageSize.toString())

		return handleQueryResult(
			this.axiosClient.get<{
				importApiResponses: SpireIntegrationApiResponse[]
				total: number
			}>(
				`/api/admin/integrations/bridge/spire/responses?${query.toString()}`
			)
		)
	}

	public deleteAllApiResponses = () =>
		handleQueryResult(
			this.axiosClient.delete(
				"/api/admin/integrations/bridge/spire/clear"
			)
		)
}

export default SpireIntegrationsClient
